import { fetchChannelSearchResults } from "@mirage/service-dbx-api";
import { tagged } from "@mirage/service-logging";
import { getChannelRecommendations } from "@mirage/service-typeahead-search";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PeopleSearchStatus } from "./usePeopleSearch";

import type { ChannelForFilter } from "@mirage/shared/search/search-filters";
const logger = tagged("useChannelSearch");
const SEARCH_DEBOUNCE_MS = 100;

export const useChannelSearch = () => {
  const [channelQuery, setChannelQuery] = useState<string>("");
  const [channelRecs, setChannelRecs] = useState<ChannelForFilter[]>([]);
  const [channelResults, setChannelResults] = useState<ChannelForFilter[]>([]);
  const [channelRecStatus, setChannelRecStatus] = useState<PeopleSearchStatus>(
    PeopleSearchStatus.INITIAL,
  );
  const [channelSearchStatus, setChannelSearchStatus] =
    useState<PeopleSearchStatus>(PeopleSearchStatus.INITIAL);

  useEffect(() => {
    const fetchChannelRecs = async () => {
      try {
        logger.debug("[search] - fetching channel recommendations");
        setChannelRecStatus(PeopleSearchStatus.LOADING);
        const channelResponse = await getChannelRecommendations();
        setChannelRecs(channelResponse);
        setChannelRecStatus(PeopleSearchStatus.SUCCESS);
      } catch (err) {
        logger.error("Error fetching channel recommendations:", err);
        setChannelRecs([]);
        setChannelRecStatus(PeopleSearchStatus.ERROR);
      }
    };

    fetchChannelRecs();
  }, []);

  const handleSearchChannelsDebounced = useCallback(
    debounce(async (query: string) => {
      logger.debug("[search] - searching for channels");
      try {
        setChannelSearchStatus(PeopleSearchStatus.LOADING);
        const channelResponse = await fetchChannelSearchResults(query);
        setChannelResults(channelResponse);
        setChannelSearchStatus(PeopleSearchStatus.SUCCESS);
      } catch (err) {
        logger.debug("[search] - caught error with channel search", err);
        setChannelResults([]);
        setChannelSearchStatus(PeopleSearchStatus.ERROR);
      }
    }, SEARCH_DEBOUNCE_MS),
    [],
  );

  const isZeroQuery = useMemo(() => {
    return channelQuery.trim() === "";
  }, [channelQuery]);

  useEffect(() => {
    if (isZeroQuery) {
      setChannelResults([]);
    } else {
      handleSearchChannelsDebounced(channelQuery);
    }
  }, [channelQuery, isZeroQuery]);

  const channelsToDisplay = useMemo(() => {
    if (isZeroQuery) {
      return channelRecs;
    }
    return channelResults;
  }, [isZeroQuery, channelRecs, channelResults]);

  const statusToDisplay = useMemo(() => {
    if (isZeroQuery) {
      return channelRecStatus;
    }
    return channelSearchStatus;
  }, [isZeroQuery, channelRecStatus, channelSearchStatus]);

  useEffect(() => {
    return () => {
      handleSearchChannelsDebounced.cancel();
    };
  }, [handleSearchChannelsDebounced]);

  return {
    channelQuery,
    setChannelQuery,
    isZeroQuery,
    channels: channelsToDisplay,
    searchStatus: statusToDisplay,
  };
};
